import { db } from "@/main";
import { getClientRef } from "@/data/repository/ClientRepository";
import Vehicle from "@/data/models/Vehicle";

function getVehiclesRef() {
  return db.doc(getClientRef()).collection("vehicles");
}

function getVehicleRef(vehicleId: string) {
  return getVehiclesRef().doc(vehicleId);
}

export async function updateVehicle(vehicle: Vehicle) {
  return getVehicleRef(vehicle.id).set(vehicle, { merge: true });
}

export async function updateVehicleById(vehicleId: string, update: Partial<Vehicle>) {
  return getVehicleRef(vehicleId).set(update, { merge: true });
}

/**
 * Updates multiple vehicles using Firestore batch operation.
 *
 * @param updates - An array of objects, each containing vehicleId and partial update data.
 */
export async function updateVehiclesBatch(
  updates: Array<{ vehicleId: string; update: Partial<Vehicle> }>
) {
  const batch = db.batch();

  updates.forEach(({ vehicleId, update }) => {
    const vehicleRef = getVehicleRef(vehicleId);
    batch.set(vehicleRef, update, { merge: true });
  });

  await batch.commit();
}

export function deleteVehicle(vehicleId: string) {
  return getVehicleRef(vehicleId).delete();
}

export function observeVehicles(
  onNewVehiclesRecievedCallback: (vehicle: Vehicle[]) => void
) {
  return getVehiclesRef()
    .orderBy("vehicleSortValue", "asc")
    .onSnapshot((snapshot) => {
      onNewVehiclesRecievedCallback(
        snapshot.docs.map((doc) => doc.data() as Vehicle)
      );
    });
}

/**
 * Fetches all vehicles once (no real-time updates).
 *
 * @returns A promise that resolves to the full, sorted list of `Vehicle`s.
 */
export async function getAllVehiclesOfClient(): Promise<Vehicle[]> {
  const snapshot = await getVehiclesRef()
    .orderBy('vehicleSortValue', 'asc')
    .get();

  return snapshot.docs.map((doc) => doc.data() as Vehicle);
}

export async function getVehicleById(id: string): Promise<Vehicle | null> {
  const snapshot = await getVehiclesRef().doc(id).get();
  if (!snapshot.exists) {
    return null;
  }
  return snapshot.data() as Vehicle;
}

export async function removeDeviceFromVehicle(vehicleId: string, deviceId: string) {
  const vehicle = await getVehicleById(vehicleId);

  if (!vehicle) {
    return;
  }

  const vehicleDevices = vehicle.devices ?? [];
  const index = vehicleDevices.indexOf(deviceId);
  if (index > -1) {
    vehicleDevices.splice(index, 1);
  }

  await updateVehicleById(vehicleId, { devices: vehicleDevices })
}


export async function removeDevicesFromVehicleMapBatch(
  devicesMap: Map<string, string[]>
) {
  const batch = db.batch();

  for (const [vehicleId, deviceIds] of devicesMap.entries()) {
    const vehicle = await getVehicleById(vehicleId);

    if (!vehicle) {
      continue;
    }

    const vehicleDevices = vehicle.devices ?? [];
    const updatedDevices = vehicleDevices.filter(
      (deviceId) => !deviceIds.includes(deviceId)
    );

    if (updatedDevices.length !== vehicleDevices.length) {
      const vehicleRef = db
        .doc(getClientRef())
        .collection("vehicles")
        .doc(vehicleId);
      batch.set(vehicleRef, { devices: updatedDevices }, { merge: true });
    }
  }

  await batch.commit();
}